import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { ProcurementOverview } from "../types"

import {
  Banknote,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Clock,
  GitBranch,
  Globe,
  MapPin,
  Users,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import { TenderAiDisclaimer } from "./tender-ai-disclaimer"
import { formatDeadline } from "../utils/deadline"

type MetaRowProps = {
  icon: React.ReactNode
  label: string
  value: string
  isManual?: boolean
}

function MetaRow({ icon, label, value, isManual = false }: MetaRowProps) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-muted-foreground mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{value}</p>
          {isManual ? <Badge variant="outline">{t("tender.snabbanalys.manual")}</Badge> : null}
        </div>
      </div>
    </div>
  )
}

type ScopeSummaryRowProps = {
  value: string
}

const SCOPE_SUMMARY_MAX_LENGTH = 220

function ScopeSummaryRow({ value }: ScopeSummaryRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const isExpandable = value.length > SCOPE_SUMMARY_MAX_LENGTH
  const displayValue = expanded ? value : `${value.slice(0, SCOPE_SUMMARY_MAX_LENGTH)}...`

  return (
    <div className="flex items-start gap-2.5">
      <span className="text-muted-foreground mt-0.5 shrink-0">
        <Briefcase className="size-3.5" />
      </span>
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{t("tender.snabbanalys.scope")}</p>
        {!isExpandable ? (
          <p className="text-sm leading-relaxed font-medium break-words">{value}</p>
        ) : (
          <p className="text-sm leading-relaxed font-medium break-words">
            {displayValue}
            <Button
              className="text-primary ml-1 h-auto p-0 text-xs font-medium hover:underline"
              type="button"
              variant="link"
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? t("tender.snabbanalys.showLess") : t("tender.snabbanalys.showMore")}
            </Button>
          </p>
        )}
      </div>
    </div>
  )
}

type SnabbanalysCardProps = {
  overview: ProcurementOverview
  submissionDeadlineIsManual?: boolean
  /** The case's "Sista dag för frågor" (YYYY-MM-DD), when one was entered. */
  questionsDeadline?: string | null
}

export function SnabbanalysCard({
  overview,
  submissionDeadlineIsManual = false,
  questionsDeadline = null,
}: SnabbanalysCardProps) {
  const { t, i18n } = useTranslation()
  const leftItems: MetaRowProps[] = []
  const rightItems: MetaRowProps[] = []
  const scopeSummary = overview.scopeSummary

  if (overview.client) {
    leftItems.push({
      icon: <Building2 className="size-3.5" />,
      label: t("tender.snabbanalys.client"),
      value: overview.client,
    })
  }
  if (overview.intermediary) {
    leftItems.push({
      icon: <Globe className="size-3.5" />,
      label: t("tender.snabbanalys.intermediary"),
      value: overview.intermediary,
    })
  }
  if (overview.domain) {
    leftItems.push({
      icon: <Briefcase className="size-3.5" />,
      label: t("tender.snabbanalys.domain"),
      value: overview.domain,
    })
  }
  if (overview.procurementType) {
    leftItems.push({
      icon: <GitBranch className="size-3.5" />,
      label: t("tender.snabbanalys.procurementType"),
      value: overview.procurementType,
    })
  }
  if (overview.contractStandard) {
    leftItems.push({
      icon: <CalendarDays className="size-3.5" />,
      label: t("tender.snabbanalys.contractStandard"),
      value:
        overview.contractStandard + (overview.frameworkRef ? ` · ${overview.frameworkRef}` : ""),
    })
  }
  if (overview.contractDuration) {
    leftItems.push({
      icon: <CalendarRange className="size-3.5" />,
      label: t("tender.snabbanalys.contractDuration"),
      value: overview.contractDuration,
    })
  }
  if (overview.optionDescription) {
    leftItems.push({
      icon: <Clock className="size-3.5" />,
      label: t("tender.snabbanalys.options"),
      value: overview.optionDescription,
    })
  }

  if (overview.estimatedValue) {
    rightItems.push({
      icon: <Banknote className="size-3.5" />,
      label: t("tender.snabbanalys.estimatedValue"),
      value: overview.estimatedValue,
    })
  }
  if (overview.positionsCount != null) {
    rightItems.push({
      icon: <Users className="size-3.5" />,
      label: t("tender.snabbanalys.positionsCount"),
      value: String(overview.positionsCount),
    })
  }
  if (overview.workModel) {
    rightItems.push({
      icon: <MapPin className="size-3.5" />,
      label: t("tender.snabbanalys.workModel"),
      value: overview.workModel,
    })
  }
  if (overview.submissionDeadline) {
    rightItems.push({
      icon: <CalendarClock className="size-3.5" />,
      label: t("tender.snabbanalys.submissionDeadline"),
      value: formatDeadline(overview.submissionDeadline, i18n.language),
      isManual: submissionDeadlineIsManual,
    })
  }
  if (questionsDeadline) {
    rightItems.push({
      icon: <CalendarClock className="size-3.5" />,
      label: t("tender.snabbanalys.questionsDeadline"),
      value: formatDeadline(questionsDeadline, i18n.language),
    })
  }

  const hasMetadata = leftItems.length > 0 || rightItems.length > 0 || Boolean(scopeSummary)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          {t("tender.snabbanalys.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <p className="text-sm leading-relaxed">{overview.description}</p>

        {hasMetadata && (
          <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <div className="space-y-3">
              {leftItems.map((item) => (
                <MetaRow key={item.label} {...item} />
              ))}
            </div>
            <div className="space-y-3">
              {rightItems.map((item) => (
                <MetaRow key={item.label} {...item} />
              ))}
              {scopeSummary && <ScopeSummaryRow value={scopeSummary} />}
            </div>
          </div>
        )}

        {overview.highlights.length > 0 && (
          <ul className="space-y-1 border-t pt-3">
            {overview.highlights.map((h, i) => (
              <li key={i} className="text-muted-foreground flex items-start gap-1.5 text-xs">
                <span className="bg-muted-foreground/40 mt-1.5 size-1 shrink-0 rounded-full" />
                {h}
              </li>
            ))}
          </ul>
        )}

        <TenderAiDisclaimer className="border-t pt-3" />
      </CardContent>
    </Card>
  )
}
